<script setup lang="ts">
import type { BuffInfoType } from '@/types/fight'
import { nextTick, ref, watch } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import FightSkillInfo from './FightSkillInfo.vue'
import FightBuffInfo from './FightBuffInfo.vue'
import FightRealAttr from './FightRealAttr.vue'

const userStore = useUserStore()

const props = withDefaults(
  defineProps<{
    realUserAttr: any
    buffInfoList: BuffInfoType[]
    debuffInfoList: BuffInfoType[]
    fightMessageList: string[]
    mp: number
    cdList: [number, number][]
    messageBoxId: string
    autoScroll?: boolean
  }>(),
  {
    autoScroll: true,
  },
)

const messageBoxRef = ref<HTMLDivElement>()
const isAutoScrollPaused = ref(false)

const resumeAutoScroll = () => {
  const messageBoxEl = messageBoxRef.value
  if (!messageBoxEl) return
  isAutoScrollPaused.value = false
  messageBoxEl.scrollTo({
    top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
    behavior: 'smooth',
  })
}

const toggleAutoScroll = () => {
  if (isAutoScrollPaused.value) {
    resumeAutoScroll()
    return
  }
  isAutoScrollPaused.value = true
}

watch(
  () => props.fightMessageList.length,
  async () => {
    const messageBoxEl = messageBoxRef.value
    if (!messageBoxEl) return

    if (!props.autoScroll || isAutoScrollPaused.value) return

    await nextTick()

    messageBoxEl.scrollTo({
      top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
      behavior: 'smooth',
    })
  },
)
</script>

<template>
  <div class="fight-res-info-wrap">
    <h4>战斗信息</h4>
    <FightRealAttr :real-user-attr="realUserAttr" v-if="userStore.soulType > 3" />
    <el-button
      size="small"
      :type="isAutoScrollPaused ? 'success' : 'info'"
      plain
      @click="toggleAutoScroll"
      class="scroll-control-btn"
      :style="userStore.soulType > 3 ? 'left: 70px;' : 'left: 4px;'"
    >
      {{ isAutoScrollPaused ? '继续' : '暂停' }}
    </el-button>
    <FightBuffInfo :buff-info-list="buffInfoList" :debuff-info-list="debuffInfoList" v-if="userStore.soulType > 3" />
    <div ref="messageBoxRef" class="fight-res-info affix" :id="messageBoxId">
      <p v-for="(item, index) of fightMessageList" :key="index" v-html="item"></p>
    </div>
    <FightSkillInfo :mp="mp" :cd-list="cdList" v-if="userStore.soulType > 3" />
  </div>
</template>

<style lang="scss" scoped>
.fight-res-info-wrap {
  border: 2px var(--fontColor) solid;
  border-radius: 5px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  position: relative;

  .scroll-control-btn {
    position: absolute;
    top: 4px;
    left: 70px;
    z-index: 1;
  }

  .fight-res-info {
    flex: 1;
    max-height: 100%;
    overflow: auto;
    padding: 0 20px 20px 20px;

    p {
      font-size: 14px;
      margin: 4px 0;
    }
  }
}
</style>
